// QuickBooks OAuth Callback Page
// Handles the redirect from QuickBooks after user authorization

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { trpc } from '../../../lib/trpc';

type CallbackStatus = 'processing' | 'success' | 'error';

export function QuickBooksCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState('');

  const completeMutation = trpc.erp.completeQuickBooksOAuth.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        setStatus('success');
        // Redirect to ERP connections page after a brief delay
        setTimeout(() => {
          navigate('/erp', { replace: true });
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage(data.error ?? 'Connection test failed after authorization.');
      }
    },
    onError: (error) => {
      setStatus('error');
      setErrorMessage(error.message);
    },
  });

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const realmId = searchParams.get('realmId');
    const error = searchParams.get('error');

    // Handle error from QuickBooks
    if (error) {
      setStatus('error');
      const errorDescription = searchParams.get('error_description') ?? 'Authorization was denied or failed.';
      setErrorMessage(errorDescription);
      return;
    }

    // Validate required parameters
    if (!code || !state || !realmId) {
      setStatus('error');
      setErrorMessage('Missing required parameters from QuickBooks authorization.');
      return;
    }

    // Exchange code for tokens
    completeMutation.mutate({ code, state, realmId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-auto p-8">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          {status === 'processing' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Connecting to QuickBooks...
              </h2>
              <p className="text-gray-600 text-sm">
                Please wait while we complete the authorization process.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                QuickBooks Connected!
              </h2>
              <p className="text-gray-600 text-sm">
                Your QuickBooks account has been successfully connected. Redirecting...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                {errorMessage}
              </p>
              <button
                onClick={() => navigate('/erp', { replace: true })}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
              >
                Return to ERP Connections
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
